<template>
  <div class="min-h-screen bg-white text-ink-900">
    <!-- Hero — matches the editorial hero pattern used site-wide (Features/
         Pricing/GEO pages): warm gradient fade + grid texture + eyebrow +
         font-display heading, not a flat solid-black banner. Left-aligned
         (not centered like the sales-oriented marketing pages) to match the
         max-w-3xl reading column the actual policy content sits in below. -->
    <section class="relative bg-hero-fade pt-16 pb-12 md:pt-20 md:pb-14">
      <div class="absolute inset-0 bg-grid-ink bg-grid-32 opacity-40 [mask-image:radial-gradient(ellipse_at_top_left,black_35%,transparent_70%)]"></div>
      <div class="relative container-wide max-w-3xl">
        <div class="eyebrow w-fit mb-4 inline-flex items-center gap-1.5">
          <Icon :name="heroIcon" :size="12" /> {{ heroLabel }}
        </div>
        <h1 class="font-display text-4xl md:text-5xl text-ink-900 leading-tight text-balance">{{ title }}</h1>
        <p v-if="subtitle" class="mt-4 text-lg text-ink-600 max-w-2xl text-pretty">{{ subtitle }}</p>
        <div class="mt-5 inline-flex items-center gap-1.5 text-xs text-ink-500 bg-white/70 border border-ink-100 rounded-full px-3 py-1">
          <Icon name="clock" :size="12" /> Last updated: {{ lastUpdated }}
        </div>
      </div>
    </section>

    <!-- Content -->
    <section class="py-12 md:py-16">
      <div class="container-wide max-w-3xl">

        <!-- Privacy Policy -->
        <template v-if="page === 'privacy'">
          <LegalSection title="1. Introduction">
            <p>LeadRanks ("<strong>we</strong>", "<strong>our</strong>", "<strong>us</strong>") is committed to protecting your privacy in accordance with the <em>Privacy Act 1988 (Cth)</em> and the Australian Privacy Principles (APPs). This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our website, platform, and services (collectively, the "<strong>Service</strong>").</p>
            <p>By using the Service, you consent to the collection and use of information in accordance with this policy. If you do not agree with any part of this policy, you should not use the Service.</p>
          </LegalSection>

          <LegalSection title="2. Information We Collect">
            <h4 class="text-sm font-semibold text-ink-900 mb-2">2.1 Personal Information</h4>
            <p>We may collect the following personal information when you create an account, use our Service, or communicate with us:</p>
            <ul>
              <li><strong>Identity data:</strong> your name, username, or similar identifier</li>
              <li><strong>Contact data:</strong> email address, billing address, and phone number</li>
              <li><strong>Financial data:</strong> payment card details and billing history (processed securely by our payment provider, Stripe)</li>
              <li><strong>Profile data:</strong> preferences, feedback, and survey responses</li>
              <li><strong>Technical data:</strong> IP address, browser type and version, time zone setting, operating system, and device information</li>
            </ul>
            <h4 class="text-sm font-semibold text-ink-900 mb-2 mt-5">2.2 Usage Data</h4>
            <p>We automatically collect information about how you interact with the Service, including:</p>
            <ul>
              <li>Pages visited, features used, and time spent on the Service</li>
              <li>URLs analysed and report data generated through the platform</li>
              <li>Diagnostic data such as error logs and performance metrics</li>
            </ul>
          </LegalSection>

          <LegalSection title="3. How We Collect Information">
            <p>We collect information:</p>
            <ul>
              <li><strong>Directly from you:</strong> when you create an account, subscribe, fill out a form, or contact us</li>
              <li><strong>Automatically:</strong> through cookies, server logs, and analytics tools as you navigate the Service</li>
              <li><strong>From third parties:</strong> such as payment processors (Stripe) and analytics providers (Google Analytics)</li>
            </ul>
          </LegalSection>

          <LegalSection title="4. How We Use Your Information">
            <p>We use your information for the following purposes:</p>
            <ul>
              <li>To provide, maintain, and improve the Service</li>
              <li>To process payments and manage your account</li>
              <li>To communicate with you about your account, updates, and support requests</li>
              <li>To generate and deliver SEO analysis reports</li>
              <li>To monitor usage patterns, detect fraud, and ensure the security of the Service</li>
              <li>To comply with legal obligations and enforce our Terms of Service</li>
            </ul>
          </LegalSection>

          <LegalSection title="5. Disclosure of Information">
            <p>We do not sell, trade, or rent your personal information to third parties. We may disclose your information in the following circumstances:</p>
            <ul>
              <li><strong>Service providers:</strong> trusted third parties who help us operate the Service (payment processors, hosting providers, email services), bound by confidentiality agreements</li>
              <li><strong>Legal compliance:</strong> as required by Australian law, court order, or government request</li>
              <li><strong>Business transfers:</strong> in connection with a merger, sale, or acquisition of LeadRanks</li>
              <li><strong>With your consent:</strong> for any other purpose with your express permission</li>
            </ul>
          </LegalSection>

          <LegalSection title="6. Data Storage and Security">
            <p>Your data is stored on secure servers located in Australia and/or the United States. We implement industry-standard technical and organisational measures to protect your information, including:</p>
            <ul>
              <li>Encryption in transit (TLS 1.3) and at rest (AES-256)</li>
              <li>Regular security audits and penetration testing</li>
              <li>Access controls and two-factor authentication for administrative personnel</li>
              <li>Automated backup and disaster recovery procedures</li>
            </ul>
            <p>While we strive to protect your personal information, no method of transmission or storage is 100% secure. We cannot guarantee absolute security.</p>
          </LegalSection>

          <LegalSection title="7. Data Retention">
            <p>We retain your personal information only as long as necessary to fulfil the purposes described in this policy, or as required by law. When your account is deleted:</p>
            <ul>
              <li>Your personal profile data is removed within 30 days</li>
              <li>SEO report data is anonymised after 90 days of account closure</li>
              <li>Financial records are retained for 7 years as required by Australian tax law</li>
            </ul>
          </LegalSection>

          <LegalSection title="8. Cookies and Tracking">
            <p>We keep cookies and similar technologies to a minimum, and use them only to operate the Service and to understand, in aggregate, how it is used.</p>
            <ul>
              <li><strong>Essential cookies</strong> — required for the Service to work securely, such as keeping you signed in and protecting forms against cross-site request forgery (CSRF). These cannot be disabled while you are using an account.</li>
              <li><strong>Local preference storage</strong> — small, non-identifying settings saved in your browser (for example, whether you have completed the onboarding tour). These are not transmitted to us.</li>
            </ul>
            <p>For analytics we use two services:</p>
            <ul>
              <li><strong>Cloudflare Web Analytics</strong> — cookieless. It records aggregate page views, referring sites, and Core Web Vitals without setting cookies and without collecting or storing personal information. It cannot identify you, and it does not track you across other websites.</li>
              <li><strong>Google Analytics 4</strong> (provided by Google LLC) — uses cookies such as <code>_ga</code> to measure how the website is used: pages visited, an approximate location derived from your IP address, device and browser type, and the site or campaign that referred you. This information may be processed by Google on servers outside Australia. We use it only in aggregate to understand and improve the Service; we do not use it to identify you or to make automated decisions about you.</li>
            </ul>
            <p>You can control or delete cookies through your browser settings, and you can opt out of Google Analytics on all websites using Google's <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener" class="text-brand-600 hover:text-brand-700">Analytics Opt-out Browser Add-on</a>. Because Cloudflare Web Analytics is cookieless, no opt-out is required for it. Disabling cookies may affect certain features of the Service.</p>
          </LegalSection>

          <LegalSection title="9. Your Rights">
            <p>Under the Australian Privacy Principles, you have the right to:</p>
            <ul>
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate or outdated information</li>
              <li>Request deletion of your personal information (subject to legal retention requirements)</li>
              <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC) if you believe we have breached the APPs</li>
            </ul>
            <p>To exercise these rights, contact us at <a href="mailto:privacy@leadranks.io" class="text-brand-600 hover:text-brand-700">privacy@leadranks.io</a>.</p>
          </LegalSection>

          <LegalSection title="10. International Users">
            <p>If you access the Service from outside Australia, your information may be transferred to, and processed in, Australia. By using the Service, you consent to this transfer. We take reasonable steps to ensure your data receives an adequate level of protection consistent with this policy.</p>
          </LegalSection>

          <LegalSection title="11. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. Material changes will be communicated to you via email or through a notice on the Service at least 14 days before they take effect. Continued use of the Service after changes take effect constitutes acceptance of the revised policy.</p>
          </LegalSection>

          <LegalSection title="12. Contact">
            <p>For privacy-related enquiries or complaints:</p>
            <p>
              Email: <a href="mailto:privacy@leadranks.io" class="text-brand-600 hover:text-brand-700">privacy@leadranks.io</a><br />
              Mail: LeadRanks, {{ australianAddress }}
            </p>
            <p class="mt-4 text-sm text-ink-500">If you are not satisfied with our response, you may lodge a complaint with the <a href="https://www.oaic.gov.au" target="_blank" rel="noopener" class="text-brand-600 hover:text-brand-700">OAIC</a>.</p>
          </LegalSection>
        </template>

        <!-- Terms of Service -->
        <template v-if="page === 'terms'">
          <LegalSection title="1. Acceptance of Terms">
            <p>Welcome to LeadRanks. By accessing or using our website, platform, and services (collectively, the "<strong>Service</strong>"), you agree to be bound by these Terms of Service ("<strong>Terms</strong>"). If you do not agree to these Terms, you must not access or use the Service.</p>
            <p>These Terms constitute a legally binding agreement between you ("<strong>User</strong>", "<strong>you</strong>") and LeadRanks ("<strong>LeadRanks</strong>", "<strong>we</strong>", "<strong>our</strong>").</p>
          </LegalSection>

          <LegalSection title="2. Eligibility">
            <p>You must be at least 18 years of age to use the Service. By creating an account, you represent and warrant that:</p>
            <ul>
              <li>You are at least 18 years old</li>
              <li>You have the legal capacity to enter into a binding contract</li>
              <li>Your use of the Service will comply with all applicable laws and regulations</li>
              <li>All information you provide is true, accurate, and complete</li>
            </ul>
          </LegalSection>

          <LegalSection title="3. Account Responsibilities">
            <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to:</p>
            <ul>
              <li>Use a strong, unique password for your account</li>
              <li>Notify us immediately of any unauthorised access or breach of security</li>
              <li>Not share your account credentials with any third party</li>
              <li>Not create accounts through unauthorised means (bots, automated scripts)</li>
            </ul>
            <p>We reserve the right to suspend or terminate accounts that violate these Terms or pose a security risk.</p>
          </LegalSection>

          <LegalSection title="4. Subscriptions and Payment">
            <h4 class="text-sm font-semibold text-ink-900 mb-2">4.1 Plans</h4>
            <p>LeadRanks offers both free and paid subscription plans. Features and limits vary by plan and are described on our <router-link to="/pricing" class="text-brand-600 hover:text-brand-700">Pricing page</router-link>.</p>
            <h4 class="text-sm font-semibold text-ink-900 mb-2 mt-5">4.2 Billing</h4>
            <p>Paid subscriptions are billed in advance on a recurring basis (monthly or annually). By subscribing to a paid plan, you authorise us to charge your payment method on each billing date until you cancel.</p>
            <h4 class="text-sm font-semibold text-ink-900 mb-2 mt-5">4.3 Cancellation</h4>
            <p>You may cancel your subscription at any time through your account settings. Cancellation takes effect at the end of your current billing period. No refunds are provided for partial billing periods, except as required by Australian Consumer Law.</p>
            <h4 class="text-sm font-semibold text-ink-900 mb-2 mt-5">4.4 Cooling-Off Period and Right of Withdrawal</h4>
            <p><strong>For EU/UK customers:</strong> if you are a consumer based in the European Union or United Kingdom, you have the right to withdraw from your subscription within 14 days of purchase without giving any reason. To exercise your right of withdrawal, you must inform us of your decision by contacting us at <a href="mailto:support@leadranks.io" class="text-brand-600 hover:text-brand-700">support@leadranks.io</a>. If you request immediate access to the Service during the 14-day withdrawal period and subsequently exercise your right of withdrawal, you acknowledge that you will be charged proportionally for the service provided up until you communicated your withdrawal.</p>
            <p><strong>For Australian and other customers:</strong> if you are based in Australia or any other region, you may request a full refund within 7 days of your initial subscription purchase, provided you have not actively used the Service (for example, run audits or accessed premium features). To request a refund under this policy, contact <a href="mailto:support@leadranks.io" class="text-brand-600 hover:text-brand-700">support@leadranks.io</a>. This cooling-off period applies in addition to any rights you may have under Australian Consumer Law or other applicable consumer protection legislation.</p>
            <h4 class="text-sm font-semibold text-ink-900 mb-2 mt-5">4.5 Price Changes</h4>
            <p>We reserve the right to change our pricing. We will notify you at least 30 days before any price change takes effect. Continued use after the change constitutes acceptance of the new pricing.</p>
          </LegalSection>

          <LegalSection title="5. Acceptable Use">
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any unlawful purpose or in violation of any laws</li>
              <li>Attempt to gain unauthorised access to any part of the Service, servers, or networks</li>
              <li>Interfere with or disrupt the Service, its infrastructure, or other users</li>
              <li>Use automated tools (scrapers, crawlers, bots) to extract data from the Service, except for legitimate SEO analysis of your own websites</li>
              <li>Upload or transmit malware, ransomware, or any malicious code</li>
              <li>Use the Service to analyse websites you do not own or have explicit permission to audit</li>
              <li>Resell, sublicense, or redistribute the Service without explicit written authorisation</li>
            </ul>
          </LegalSection>

          <LegalSection title="6. Intellectual Property">
            <h4 class="text-sm font-semibold text-ink-900 mb-2">6.1 Our Content</h4>
            <p>The Service, including its code, design, branding, algorithms, reports, and documentation, is owned by LeadRanks and protected by Australian and international copyright and intellectual property laws. You may not copy, modify, distribute, or create derivative works without our prior written consent.</p>
            <h4 class="text-sm font-semibold text-ink-900 mb-2 mt-5">6.2 Your Content</h4>
            <p>You retain ownership of all data, URLs, and content you submit to the Service. By uploading or submitting content, you grant LeadRanks a limited, worldwide, royalty-free licence to use, process, and display your content solely for the purpose of providing and improving the Service.</p>
            <h4 class="text-sm font-semibold text-ink-900 mb-2 mt-5">6.3 Feedback</h4>
            <p>Any feedback, suggestions, or ideas you provide about the Service may be used by LeadRanks without restriction or compensation.</p>
          </LegalSection>

          <LegalSection title="7. Third-Party Services">
            <p>The Service may integrate with or link to third-party services (such as payment processors, analytics tools, and APIs). LeadRanks is not responsible for the availability, accuracy, or practices of third-party services. Your use of third-party services is governed by their respective terms and policies.</p>
          </LegalSection>

          <LegalSection title="8. Disclaimer of Warranties">
            <p>To the maximum extent permitted by law, the Service is provided on an "as is" and "as available" basis. LeadRanks makes no warranties, express or implied, regarding:</p>
            <ul>
              <li>The accuracy, completeness, or reliability of SEO analysis reports</li>
              <li>The uninterrupted or error-free operation of the Service</li>
              <li>The suitability of the Service for any particular purpose</li>
              <li>The absence of viruses or other harmful components</li>
            </ul>
            <p>SEO rankings and search engine behaviour are inherently variable. We provide estimates and recommendations based on current data; results are not guaranteed.</p>
          </LegalSection>

          <LegalSection title="9. Limitation of Liability">
            <p>To the maximum extent permitted by law, LeadRanks and its directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service, including but not limited to:</p>
            <ul>
              <li>Loss of revenue, profits, goodwill, or business opportunities</li>
              <li>Loss or corruption of data</li>
              <li>Any damages resulting from reliance on SEO analysis outputs</li>
            </ul>
            <p>Our total aggregate liability to you for any claims arising from these Terms or the Service shall not exceed the amount you paid us in the 12 months preceding the claim. This limitation does not apply where prohibited by Australian Consumer Law.</p>
            <p class="mt-4 p-4 bg-brand-50 border-l-4 border-brand-400 rounded-r text-sm">
              <strong>Australian Consumer Law:</strong> These Terms do not exclude, restrict, or modify any consumer guarantee, right, or remedy that cannot be lawfully excluded under the Australian Consumer Law (Schedule 2 of the <em>Competition and Consumer Act 2010 (Cth)</em>). If you are a "consumer" under the ACL, our liability for a breach of a consumer guarantee is limited to the re-supply of the Service or the cost of having the Service supplied again.
            </p>
          </LegalSection>

          <LegalSection title="10. Indemnification">
            <p>You agree to indemnify and hold harmless LeadRanks and its affiliates from any claims, damages, losses, or expenses (including reasonable legal fees) arising from:</p>
            <ul>
              <li>Your use of the Service in violation of these Terms</li>
              <li>Your violation of any applicable law or third-party right</li>
              <li>Content you submit that infringes on intellectual property or privacy rights</li>
            </ul>
          </LegalSection>

          <LegalSection title="11. Termination">
            <p>We may suspend or terminate your access to the Service:</p>
            <ul>
              <li>For violation of these Terms, with or without notice</li>
              <li>For non-payment of subscription fees</li>
              <li>At our discretion with 30 days' notice, for any reason</li>
            </ul>
            <p>Upon termination, your right to access the Service ceases immediately. Provisions regarding intellectual property, disclaimers, liability limitations, and indemnification survive termination.</p>
          </LegalSection>

          <LegalSection title="12. Dispute Resolution">
            <p>Any dispute arising from these Terms shall first be attempted to be resolved through informal negotiation. If unresolved within 30 days, the parties agree to submit the dispute to mediation administered by the Australian Disputes Centre (ADC) before commencing legal proceedings.</p>
            <p>These Terms are governed by the laws of Victoria, Australia. You agree to submit to the exclusive jurisdiction of the courts of Victoria.</p>
          </LegalSection>

          <LegalSection title="13. Changes to Terms">
            <p>We may modify these Terms at any time. Material changes will be communicated via email or through a notice on the Service at least 30 days in advance. Continued use after changes take effect constitutes acceptance of the revised Terms.</p>
          </LegalSection>

          <LegalSection title="14. General">
            <ul>
              <li><strong>Severability:</strong> If any provision is found to be unenforceable, the remaining provisions remain in full effect.</li>
              <li><strong>Waiver:</strong> Failure to enforce any provision does not constitute a waiver of that right.</li>
              <li><strong>Assignment:</strong> You may not assign these Terms without our written consent. We may assign these Terms to an affiliate or successor.</li>
              <li><strong>Entire agreement:</strong> These Terms, together with our Privacy Policy and SLA, constitute the entire agreement between you and LeadRanks.</li>
            </ul>
          </LegalSection>
        </template>

        <!-- Service Level Agreement -->
        <template v-if="page === 'sla'">
          <LegalSection title="1. Scope">
            <p>This Service Level Agreement ("<strong>SLA</strong>") defines the service commitments, uptime targets, and support parameters for LeadRanks ("<strong>Service</strong>"). This SLA applies to paid subscription plans only. Free-tier accounts are provided on a best-effort basis with no availability commitments.</p>
          </LegalSection>

          <LegalSection title="2. Uptime Commitment">
            <h4 class="text-sm font-semibold text-ink-900 mb-2">2.1 Platform Availability</h4>
            <p>LeadRanks commits to <strong>99.5% platform availability</strong> (measured monthly), excluding Scheduled Maintenance.</p>
            <div class="mt-3 mb-5 overflow-x-auto">
              <table class="w-full text-sm border-collapse border border-ink-200 rounded-lg">
                <thead>
                  <tr class="bg-ink-50">
                    <th class="text-left p-3 border border-ink-200 font-semibold">Plan</th>
                    <th class="text-left p-3 border border-ink-200 font-semibold">Uptime SLA</th>
                    <th class="text-left p-3 border border-ink-200 font-semibold">Credit (if breached)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="p-3 border border-ink-200">Free</td>
                    <td class="p-3 border border-ink-200">Best effort</td>
                    <td class="p-3 border border-ink-200">N/A</td>
                  </tr>
                  <tr>
                    <td class="p-3 border border-ink-200">Starter</td>
                    <td class="p-3 border border-ink-200">99.5%</td>
                    <td class="p-3 border border-ink-200">5% of monthly fee</td>
                  </tr>
                  <tr>
                    <td class="p-3 border border-ink-200">Professional</td>
                    <td class="p-3 border border-ink-200">99.7%</td>
                    <td class="p-3 border border-ink-200">10% of monthly fee</td>
                  </tr>
                  <tr>
                    <td class="p-3 border border-ink-200">Business</td>
                    <td class="p-3 border border-ink-200">99.9%</td>
                    <td class="p-3 border border-ink-200">15% of monthly fee</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <h4 class="text-sm font-semibold text-ink-900 mb-2 mt-5">2.2 Measurement</h4>
            <p>Platform availability is measured from our external monitoring infrastructure, polling the Service every 60 seconds from multiple geographic locations. Availability is calculated as:</p>
            <div class="bg-ink-50 border border-ink-200 rounded-lg p-4 my-4 text-center font-mono text-sm">
              Availability = (Total minutes in month − Downtime minutes) / Total minutes in month × 100%
            </div>
            <h4 class="text-sm font-semibold text-ink-900 mb-2 mt-5">2.3 Exclusions</h4>
            <p>Downtime caused by the following is excluded from availability calculations:</p>
            <ul>
              <li>Scheduled Maintenance (announced at least 48 hours in advance)</li>
              <li>Force majeure events (natural disasters, war, civil unrest)</li>
              <li>Third-party service outages beyond our reasonable control (DNS providers, CDNs, cloud providers)</li>
              <li>Issues caused by your equipment, internet connection, or browser</li>
              <li>Denial-of-service attacks or other malicious acts by third parties</li>
            </ul>
          </LegalSection>

          <LegalSection title="3. Scheduled Maintenance">
            <p>Scheduled Maintenance windows are:</p>
            <ul>
              <li><strong>Standard:</strong> Saturdays 02:00–06:00 AEDT/AEST, announced at least 48 hours before</li>
              <li><strong>Emergency:</strong> As needed with maximum 2 hours' notice (used only for critical security patches)</li>
            </ul>
            <p>We limit Scheduled Maintenance to no more than 4 hours per month. Emergency maintenance exceeding 30 minutes is counted as downtime.</p>
          </LegalSection>

          <LegalSection title="4. Support">
            <h4 class="text-sm font-semibold text-ink-900 mb-2">4.1 Support Channels</h4>
            <ul>
              <li><strong>Email:</strong> <a href="mailto:support@leadranks.io" class="text-brand-600 hover:text-brand-700">support@leadranks.io</a></li>
              <li><strong>In-app chat:</strong> Available to Professional and Business plan subscribers</li>
              <li><strong>Help centre:</strong> <router-link to="/learn" class="text-brand-600 hover:text-brand-700">leadranks.io/learn</router-link></li>
            </ul>
            <h4 class="text-sm font-semibold text-ink-900 mb-2 mt-5">4.2 Response Times</h4>
            <!-- 4 columns of real prose ("Description") doesn't fit a 375px
                 screen even inside overflow-x-auto -- the table technically
                 scrolls within its own box (confirmed: no page-level
                 blowout), but the last column is silently cut off at the
                 viewport edge with zero visual affordance that there's more
                 to scroll to. Same fix as PricingPage.vue's comparison
                 table (§9 #33): keep the real table for sm:+, stack each
                 row as a labeled card on mobile. -->
            <div class="hidden sm:block mt-3 mb-5 overflow-x-auto">
              <table class="w-full text-sm border-collapse border border-ink-200 rounded-lg">
                <thead>
                  <tr class="bg-ink-50">
                    <th class="text-left p-3 border border-ink-200 font-semibold">Severity</th>
                    <th class="text-left p-3 border border-ink-200 font-semibold">Description</th>
                    <th class="text-left p-3 border border-ink-200 font-semibold">Professional</th>
                    <th class="text-left p-3 border border-ink-200 font-semibold">Business</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in responseTimeRows" :key="row.severity">
                    <td class="p-3 border border-ink-200 font-medium" :class="row.tone">{{ row.severity }}</td>
                    <td class="p-3 border border-ink-200">{{ row.description }}</td>
                    <td class="p-3 border border-ink-200">{{ row.professional }}</td>
                    <td class="p-3 border border-ink-200">{{ row.business }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="sm:hidden mt-3 mb-5 space-y-3">
              <div v-for="row in responseTimeRows" :key="row.severity" class="border border-ink-200 rounded-lg p-3">
                <div class="font-semibold" :class="row.tone">{{ row.severity }}</div>
                <div class="text-ink-600 mt-1">{{ row.description }}</div>
                <div class="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div><span class="text-ink-400">Professional:</span> {{ row.professional }}</div>
                  <div><span class="text-ink-400">Business:</span> {{ row.business }}</div>
                </div>
              </div>
            </div>
            <p class="text-sm text-ink-500">Response times are measured during business hours: Monday–Friday, 09:00–18:00 AEST/AEDT, excluding Victorian public holidays.</p>
          </LegalSection>

          <LegalSection title="5. Service Credits">
            <p>If we fail to meet the uptime SLA for your plan in a given calendar month, you are entitled to a service credit as defined in Section 2.1.</p>
            <p>To claim a credit, you must contact <a href="mailto:support@leadranks.io" class="text-brand-600 hover:text-brand-700">support@leadranks.io</a> within 14 days of the SLA breach, including the date, time, and nature of the outage. Credits are applied to your next billing cycle and are not redeemable for cash.</p>
            <p>Credits are your sole and exclusive remedy for SLA breaches. The total credit in a given month shall not exceed 50% of your monthly subscription fee.</p>
          </LegalSection>

          <LegalSection title="6. Data Protection and Backup">
            <ul>
              <li><strong>Backup frequency:</strong> Daily automated backups with point-in-time recovery</li>
              <li><strong>Retention:</strong> 30 days of rolling backups</li>
              <li><strong>Recovery time objective (RTO):</strong> &lt; 4 hours from detection</li>
              <li><strong>Recovery point objective (RPO):</strong> &lt; 24 hours</li>
              <li><strong>Disaster recovery:</strong> Annual DR testing with documented runbooks</li>
            </ul>
          </LegalSection>

          <LegalSection title="7. Monitoring and Transparency">
            <p>We maintain a public status page at <a href="https://status.leadranks.io" target="_blank" rel="noopener" class="text-brand-600 hover:text-brand-700">status.leadranks.io</a> where you can subscribe to incident notifications. Current and historical uptime data is published monthly.</p>
          </LegalSection>

          <LegalSection title="8. Changes to This SLA">
            <p>We may update this SLA from time to time. Changes affecting your plan tier will be communicated at least 30 days in advance. Continued use after changes take effect constitutes acceptance of the revised SLA.</p>
          </LegalSection>

          <LegalSection title="9. Contact">
            <p>For SLA-related enquiries or to claim a service credit:</p>
            <p>Email: <a href="mailto:support@leadranks.io" class="text-brand-600 hover:text-brand-700">support@leadranks.io</a></p>
          </LegalSection>
        </template>

      </div>
    </section>

    <!-- CTA -->
    <section class="py-12 bg-ink-50">
      <div class="container-wide text-center">
        <p class="text-ink-500 text-sm mb-4">Questions about our policies?</p>
        <a href="mailto:support@leadranks.io" class="text-brand-600 hover:text-brand-700 font-medium">Contact our team →</a>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { usePageMeta } from '../../composables/usePageMeta.js';
import LegalSection from '../ui/LegalSection.vue';
import Icon from '../ui/Icon.vue';

const route = useRoute();
const page = computed(() => route.meta.legalType || 'privacy');

const australianAddress = 'Melbourne VIC 3000, Australia';

// SLA §4.2 Response Times -- single source feeding both the sm:+ table and
// the mobile stacked-card layout below it, so the two can never drift.
const responseTimeRows = [
  { severity: 'Critical', tone: 'text-danger-700', description: 'Service unavailable for all users', professional: '< 1 hour', business: '< 30 minutes' },
  { severity: 'High', tone: 'text-warn-700', description: 'Major feature degraded or unavailable', professional: '< 4 hours', business: '< 2 hours' },
  { severity: 'Medium', tone: 'text-brand-700', description: 'Minor feature issue, workaround exists', professional: '< 8 business hours', business: '< 4 business hours' },
  { severity: 'Low', tone: '', description: 'Cosmetic issue or general enquiry', professional: '< 2 business days', business: '< 1 business day' },
];

const config = {
  privacy: {
    heroLabel: 'Legal · Privacy',
    heroIcon: 'shield',
    title: 'Privacy Policy',
    subtitle: 'How LeadRanks collects, uses, stores, and protects your personal information under Australian privacy law, plus your rights and how to exercise them.',
    lastUpdated: '12 September 2026',
  },
  terms: {
    heroLabel: 'Legal · Terms',
    heroIcon: 'file-text',
    title: 'Terms of Service',
    subtitle: 'The rules for using LeadRanks: accounts, subscriptions, acceptable use, intellectual property, cancellations, and the limits of our liability.',
    lastUpdated: '26 July 2026',
  },
  sla: {
    heroLabel: 'Legal · SLA',
    heroIcon: 'activity',
    title: 'Service Level Agreement',
    subtitle: 'Our commitments on platform availability, support response times, and service credits, including what we guarantee and what happens if we fall short.',
    lastUpdated: '26 July 2026',
  },
};

const { heroLabel, heroIcon, title, subtitle, lastUpdated } = config[page.value];

usePageMeta(() => ({
  title: `${config[page.value].title} — LeadRanks`,
  meta: [
    { name: 'description', content: config[page.value].subtitle },
    { property: 'og:title', content: `${config[page.value].title} — LeadRanks` },
    { property: 'og:description', content: config[page.value].subtitle },
    { property: 'og:image', content: 'https://leadranks.io/og-image-1200x627.png' },
  ],
}));
</script>
